/**
 * KAN-829/KAN-830: бот для менеджеров (один бот платформы). Статус бота
 * (только чтение); ссылка привязки Telegram менеджера и отвязка (карточка
 * менеджера). После отвязки справочник
 * и карточка менеджера перечитываются (MANAGERS_QUERY_KEY). Ошибки сервера
 * отдаются текстом для показа в блоке.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { managerBotApi } from '@/services/api/managerBot';
import { MANAGERS_QUERY_KEY } from '@/services/api/queryKeys';
import { extractErrorMessage } from '@/lib/extractErrorMessage';

export const MANAGER_BOT_QUERY_KEY = ['manager-bot'] as const;

function errorText(err: unknown, fallback: string): string | null {
  return err ? extractErrorMessage(err, fallback) : null;
}

/** Статус бота платформы. enabled=false: запрос не уходит (роль без доступа). */
export function useManagerBotStatus(enabled = true) {
  return useQuery({
    queryKey: MANAGER_BOT_QUERY_KEY,
    queryFn: managerBotApi.getStatus,
    enabled,
  });
}

/** Привязка Telegram менеджера: получить ссылку, отвязать. */
export function useManagerTelegramLink(managerId: string) {
  const client = useQueryClient();

  const createLink = useMutation({
    mutationFn: () => managerBotApi.createManagerLink(managerId),
  });
  const unlink = useMutation({
    mutationFn: () => managerBotApi.unlinkManager(managerId),
    onSuccess: () => {
      createLink.reset();
      void client.invalidateQueries({ queryKey: MANAGERS_QUERY_KEY });
    },
  });

  return {
    createLink,
    unlink,
    error:
      errorText(createLink.error, 'Не удалось получить ссылку') ??
      errorText(unlink.error, 'Не удалось отключить Telegram'),
  };
}

/** Копирование ссылки в буфер обмена; false, если буфер недоступен. */
export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}
